import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem, TextField, InputAdornment, Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

const DifferentAllocationOrders = ({ 
  investorOrders, 
  assetOptions, 
  handleInvestorFieldChange, 
  // removeModalOpen, 
  // handleCloseRemoveModal, 
  // investorToRemove, 
  strategyBreakups,
  setInvestorOrders,
  onAllocationError,
  onCanProceedChange,
  loading = false
}) => {
  // State for tracking deleted assets per investor
  const [deletedAssetsPerInvestor, setDeletedAssetsPerInvestor] = useState({});
  const [hasDeletedAssetPerInvestor, setHasDeletedAssetPerInvestor] = useState({});
  
  // State for remove investor modal
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [investorToRemove, setInvestorToRemove] = useState(null);

  // Input validation functions
  const validateNumericInput = (value) => {
    // Allow empty string, numbers, and decimal points
    return /^$|^[0-9]*\.?[0-9]*$/.test(value);
  };

  const validateAllocationInput = (value) => {
    // Allow empty string, numbers, and decimal points
    // Don't restrict to 100 during typing - let user type any number
    return /^$|^[0-9]*\.?[0-9]*$/.test(value);
  };

  const handleAllocationChange = (investorIndex, orderIndex, value) => {
    if (validateAllocationInput(value)) {
      handleOrderFieldChange(investorIndex, orderIndex, 'allocation', value);
    }
  };

  const handleLimitPriceChange = (investorIndex, orderIndex, value) => {
    if (validateNumericInput(value)) {
      handleOrderFieldChange(investorIndex, orderIndex, 'limitPrice', value);
    }
  };
  
  // Initialize orders from breakups if present and orders are empty
  useEffect(() => {
    if (strategyBreakups && investorOrders.length > 0) {
      // Check if any investor has empty orders (no asset selected)
      const hasEmptyOrders = investorOrders.some(investor => !investor.orders || investor.orders.length === 0);
      
      if (hasEmptyOrders) {
        const updatedInvestors = investorOrders.map(investor => {
          // If this investor has no orders, initialize with breakups
          if (!investor.orders || investor.orders.length === 0) {
            return {
              ...investor,
              orderType: '',
              orders: strategyBreakups.map(b => ({
                orderType: '',
                asset: b.asset.symbol,
                allocation: b.proportion,
                limitPrice: '',
                limitPriceFocused: false
              }))
            };
          }
          return investor;
        });
        
        setInvestorOrders(updatedInvestors);
      }
    }
  }, [strategyBreakups, investorOrders, setInvestorOrders]);

  // Reset component state when investorOrders are reset to initial state
  useEffect(() => {
    const hasEmptyOrders = investorOrders.some(investor => 
      !investor.orders || 
      investor.orders.length === 0 || 
      (investor.orders.length === 1 && !investor.orders[0].asset && !investor.orders[0].allocation)
    );
    
    if (hasEmptyOrders) {
      // Reset all internal state when orders are reset to initial state
      setDeletedAssetsPerInvestor({});
      setHasDeletedAssetPerInvestor({});
    }
  }, [investorOrders]);

  // Handle opening the remove investor modal
  const handleRemoveInvestor = (investor) => {
    setInvestorToRemove(investor);
    setRemoveModalOpen(true);
  };

  // Handle confirming investor removal
  const handleConfirmRemoveInvestor = () => {
    if (investorToRemove) {
      setInvestorOrders(prev => 
        prev.filter(investor => investor.userRole?.id !== investorToRemove.userRole?.id)
      );
    }
    setRemoveModalOpen(false);
    setInvestorToRemove(null);
  };

  // Handle closing the remove investor modal
  const handleCloseRemoveModal = () => {
    setRemoveModalOpen(false);
    setInvestorToRemove(null);
  };

  // Handle adding a new order for a specific investor
  const handleAddOrder = (investorIndex) => {
    const currentInvestor = investorOrders[investorIndex];
    const orders = currentInvestor.orders || [];
    
    // Check if there are available assets to add
    const usedAssets = orders.map(order => order.asset).filter(asset => asset && asset.trim() !== '') || [];
    const availableAssets = assetOptions.filter(option => !usedAssets.includes(option.value));
    
    if (availableAssets.length === 0) {
      return; // No available assets to add
    }
    
    // Check if all existing orders have required fields filled
    if (orders.length > 0) {
      const hasIncompleteOrders = orders.some(order => {
        // Check if asset is empty
        if (!order.asset || order.asset.trim() === '') {
          return true;
        }
        
        // Check if allocation is empty
        if (order.allocation === '' || order.allocation === null || order.allocation === undefined || String(order.allocation).trim() === '') {
          return true;
        }
        
        // Check if limit price is required but empty or not positive (for Limit orders)
        if (currentInvestor.orderType === 'LMT' && (!order.limitPrice || order.limitPrice.trim() === '' || Number(order.limitPrice) <= 0)) {
          return true;
        }
        
        return false;
      });
      
      if (hasIncompleteOrders) {
        return; // Don't add new asset if previous ones are incomplete
      }
    }
    
    setInvestorOrders(prev => {
      const updatedInvestors = [...prev];
      const currentInvestor = updatedInvestors[investorIndex];
      
      if (!currentInvestor.orders) {
        currentInvestor.orders = [];
      }
      
      const newOrder = {
        orderType: currentInvestor.orderType || '',
        asset: '',
        allocation: '',
        limitPrice: '',
        limitPriceFocused: false
      };
      
      currentInvestor.orders.push(newOrder);
      return updatedInvestors;
    });
  };

  // Handle removing an order for a specific investor
  const handleRemoveOrder = (investorIndex, orderIndex) => {
    setInvestorOrders(prev => {
      const updatedInvestors = [...prev];
      const currentInvestor = updatedInvestors[investorIndex];
      
      if (currentInvestor.orders && currentInvestor.orders.length > 1) {
        const assetToDelete = currentInvestor.orders[orderIndex];
        currentInvestor.orders.splice(orderIndex, 1);
        
        // Store deleted asset for potential restoration to the same investor
        // Only overwrite if the new deletion has a valid allocation, otherwise keep the previous one
        setDeletedAssetsPerInvestor(prev => {
          const currentDeletedAssets = prev[investorIndex] || [];
          const hasValidAllocation = assetToDelete.allocation !== '' && assetToDelete.allocation !== null && assetToDelete.allocation !== undefined && !isNaN(Number(assetToDelete.allocation));
          
          // Find if there's already a deleted asset with the same name
          const existingAssetIndex = currentDeletedAssets.findIndex(deletedAsset => 
            deletedAsset.asset === assetToDelete.asset
          );
          
          if (existingAssetIndex !== -1) {
            // There's already a deleted asset with this name
            const existingAsset = currentDeletedAssets[existingAssetIndex];
            const existingHasValidAllocation = existingAsset.allocation !== '' && existingAsset.allocation !== null && existingAsset.allocation !== undefined && !isNaN(Number(existingAsset.allocation));
            
            // If current deletion has valid allocation, use it
            if (hasValidAllocation) {
              const updatedAssets = [...currentDeletedAssets];
              updatedAssets[existingAssetIndex] = assetToDelete;
              return {
                ...prev,
                [investorIndex]: updatedAssets
              };
            }
            
            // If current deletion has empty allocation but previous deletion had valid allocation, keep the previous one
            if (!hasValidAllocation && existingHasValidAllocation) {
              return prev; // Keep the previous deletion with valid allocation
            }
            
            // Otherwise, use the current deletion (even if empty allocation)
            const updatedAssets = [...currentDeletedAssets];
            updatedAssets[existingAssetIndex] = assetToDelete;
            return {
              ...prev,
              [investorIndex]: updatedAssets
            };
          } else {
            // No existing asset with this name, just add it
            return {
              ...prev,
              [investorIndex]: [...currentDeletedAssets, assetToDelete]
            };
          }
        });
        setHasDeletedAssetPerInvestor(prev => ({
          ...prev,
          [investorIndex]: true
        }));
      }
      
      return updatedInvestors;
    });
  };

  // Handle order field changes
  const handleOrderFieldChange = (investorIndex, orderIndex, field, value) => {
    setInvestorOrders(prev => {
      const updatedInvestors = [...prev];
      const currentInvestor = updatedInvestors[investorIndex];
      
      if (currentInvestor.orders && currentInvestor.orders[orderIndex]) {
        // If this is an asset change, check for duplicates
        if (field === 'asset' && value) {
          // Find if this asset already exists in other orders
          const existingAssetIndex = currentInvestor.orders.findIndex((order, idx) => 
            idx !== orderIndex && order.asset === value
          );
          
          if (existingAssetIndex !== -1) {
            // Remove the existing asset entry to prevent duplicates
            currentInvestor.orders.splice(existingAssetIndex, 1);
            
            // Adjust orderIndex if it was affected by the splice
            if (existingAssetIndex < orderIndex) {
              orderIndex--;
            }
          }
        }
        
        currentInvestor.orders[orderIndex] = {
          ...currentInvestor.orders[orderIndex],
          [field]: value
        };
      }
      
      return updatedInvestors;
    });
  };

  // Handle investor order type change
  const handleInvestorOrderTypeChange = (investorIndex, orderType) => {
    setInvestorOrders(prev => {
      const updatedInvestors = [...prev];
      const currentInvestor = updatedInvestors[investorIndex];
      
      currentInvestor.orderType = orderType;
      
      // Update all orders for this investor
      if (currentInvestor.orders) {
        currentInvestor.orders = currentInvestor.orders.map(order => ({
          ...order,
          orderType: orderType,
          limitPrice: orderType === 'MKT' ? '' : order.limitPrice,
          limitPriceFocused: false
        }));
      }
      
      return updatedInvestors;
    });
    
    // Don't reset deleted assets state when changing order type
    // This preserves the deleted assets so Reset Asset button remains enabled
    // and can restore previously deleted assets when switching between order types
  };

  // Calculate allocation sum for each investor
  const getInvestorAllocationSum = (orders) => {
    if (!orders || orders.length === 0) return 0;
    return orders.reduce((sum, order) => sum + Number(order.allocation || 0), 0);
  };

  // Check if all investors have valid allocations (100% each) and order types selected
  const hasValidAllocations = () => {
    return investorOrders.every(investor => {
      // First check if order type is selected
      if (!investor.orderType || investor.orderType.trim() === '') {
        return false;
      }
      
      const orders = investor.orders || [];
      const allocationSum = getInvestorAllocationSum(orders);
      
      // Check if all orders have valid assets and allocations
      const hasValidOrders = orders.length > 0 && orders.every(order => 
        order.asset && order.asset.trim() !== '' && 
        order.allocation !== '' && order.allocation !== null && order.allocation !== undefined && 
        !isNaN(Number(order.allocation))
      );
      
      // Check if current order type is Limit and all limit prices are filled and positive
      const isLimit = investor.orderType === 'LMT';
      const allLimitPricesFilled = isLimit
        ? orders.every(order => order.limitPrice && order.limitPrice.trim() !== '' && !isNaN(Number(order.limitPrice)) && Number(order.limitPrice) > 0)
        : true;
      
      return allocationSum === 100 && hasValidOrders && (investor.orderType === 'MKT' || (isLimit && allLimitPricesFilled));
    });
  };

  // Check if any investor has allocation errors
  const hasAllocationErrors = () => {
    return investorOrders.some(investor => {
      const allocationSum = getInvestorAllocationSum(investor.orders);
      return allocationSum !== 100;
    });
  };

  // Note: Order type consistency validation removed - now supports different order types per investor

  // Get error message for specific investor
  const getInvestorErrorMessage = (investor) => {
    const allocationSum = getInvestorAllocationSum(investor.orders);
    
    // Check if any individual asset allocation exceeds 100%
    if (investor.orders && investor.orders.length > 0) {
      const overAllocatedAssets = investor.orders.filter(order => 
        order.asset && Number(order.allocation || 0) > 100
      );
      
      if (overAllocatedAssets.length > 0) {
        const assetNames = overAllocatedAssets.map(order => order.asset).join(', ');
        return `Reduce ${assetNames} allocation`;
      }
    }
    
    if (allocationSum !== 100) {
      return `Total allocation should equal 100% (currently ${allocationSum}%)`;
    }
    
    // Note: Order type consistency error check removed - now supports different order types per investor
    
    return '';
  };

  // Reset handler - restore only the most recently deleted asset for each unique asset name
  const handleReset = (investorIndex) => {
    const deletedAssets = deletedAssetsPerInvestor[investorIndex] || [];
    if (deletedAssets.length === 0) {
      setHasDeletedAssetPerInvestor(prev => ({
        ...prev,
        [investorIndex]: false
      }));
      return;
    }

    // Only restore if user explicitly wants to (when allocation is not 100% and there are available assets)
    const currentInvestor = investorOrders[investorIndex];
    const orders = currentInvestor.orders || [];
    const allocationSum = orders.length === 0 ? 100 : 
      orders.every(order => !order.allocation || order.allocation === '') ? 100 : 
      orders.reduce((sum, order) => sum + Number(order.allocation || 0), 0);
    
    const hasAvailableAssets = assetOptions.length > orders.length;
    
    // Only restore if allocation is not 100% and there are available assets
    if (allocationSum === 100 || !hasAvailableAssets) {
      return;
    }

    // Restore only the most recently deleted asset for each unique asset name
    setInvestorOrders(prev => {
      const updatedInvestors = [...prev];
      const currentInvestor = updatedInvestors[investorIndex];
      
      if (currentInvestor.orders) {
        // Get current asset names to avoid duplicates
        const currentAssetNames = currentInvestor.orders
          .map(order => order.asset)
          .filter(asset => asset && asset.trim() !== '');
        
        // Create a map to track the best deleted asset for each unique asset name
        // (best = has valid allocation, or most recent if no valid allocation)
        const bestDeletedAssets = new Map();
        
        // Process deleted assets in order (since handleRemoveOrder already handles priority)
        for (let i = 0; i < deletedAssets.length; i++) {
          const deletedAsset = deletedAssets[i];
          if (deletedAsset.asset && deletedAsset.asset.trim() !== '') {
            const hasValidAllocation = deletedAsset.allocation !== '' && deletedAsset.allocation !== null && deletedAsset.allocation !== undefined && !isNaN(Number(deletedAsset.allocation));
            
            // If we haven't seen this asset before, add it
            if (!bestDeletedAssets.has(deletedAsset.asset)) {
              bestDeletedAssets.set(deletedAsset.asset, deletedAsset);
            } else {
              // If we already have this asset, only replace if current has valid allocation and previous doesn't
              const existingAsset = bestDeletedAssets.get(deletedAsset.asset);
              const existingHasValidAllocation = existingAsset.allocation !== '' && existingAsset.allocation !== null && existingAsset.allocation !== undefined && !isNaN(Number(existingAsset.allocation));
              
              if (hasValidAllocation && !existingHasValidAllocation) {
                bestDeletedAssets.set(deletedAsset.asset, deletedAsset);
              }
            }
          }
        }
        
        // Convert map values back to array and filter out assets that already exist
        const assetsToRestore = Array.from(bestDeletedAssets.values())
          .filter(deletedAsset => !currentAssetNames.includes(deletedAsset.asset));
        
        if (assetsToRestore.length > 0) {
          // Preserve the current order type for restored assets
          const restoredAssets = assetsToRestore.map(asset => ({
            ...asset,
            orderType: currentInvestor.orderType || '',
            limitPrice: (currentInvestor.orderType || '') === 'MKT' ? '' : asset.limitPrice,
            limitPriceFocused: false
          }));
          currentInvestor.orders.push(...restoredAssets);
        }
      }
      
      return updatedInvestors;
    });
    
    // Clear deleted assets for this investor
    setDeletedAssetsPerInvestor(prev => {
      const updated = { ...prev };
      delete updated[investorIndex];
      return updated;
    });
    setHasDeletedAssetPerInvestor(prev => ({
      ...prev,
      [investorIndex]: false
    }));
  };

  // Helper function to check if there are available assets for a specific investor
  const hasAvailableAssets = (investorIndex) => {
    const currentInvestor = investorOrders[investorIndex];
    const usedAssets = currentInvestor.orders?.map(order => order.asset).filter(asset => asset && asset.trim() !== '') || [];
    const availableAssets = assetOptions.filter(option => !usedAssets.includes(option.value));
    return availableAssets.length > 0;
  };

  // Helper function to check if an investor can add assets
  const canAddAsset = (investorIndex) => {
    const currentInvestor = investorOrders[investorIndex];
    const orders = currentInvestor.orders || [];
    
    // Check if there are available assets to add
    if (!hasAvailableAssets(investorIndex)) {
      return false;
    }
    
    // If there are no orders, don't allow adding assets until at least one is properly filled
    if (orders.length === 0) {
      return false;
    }
    
    // Check if all existing orders have required fields filled
    const hasIncompleteOrders = orders.some(order => {
      // Check if asset is empty
      if (!order.asset || order.asset.trim() === '') {
        return true;
      }
      
      // Check if allocation is empty
      if (!order.allocation || String(order.allocation).trim() === '') {
        return true;
      }
      
      // Check if limit price is required but empty or not positive (for Limit orders)
      if (currentInvestor.orderType === 'LMT' && (!order.limitPrice || order.limitPrice.trim() === '' || Number(order.limitPrice) <= 0)) {
        return true;
      }
      
      return false;
    });
    
    if (hasIncompleteOrders) {
      return false;
    }
    
    return true;
  };

  // Helper function to check if an investor can reset assets
  const canResetAsset = (investorIndex) => {
    const currentInvestor = investorOrders[investorIndex];
    const orders = currentInvestor.orders || [];
    
    // Check if there are deleted assets to restore
    if (!hasDeletedAssetPerInvestor[investorIndex]) {
      return false;
    }
    
    // Check if allocation sum is 100% (no room for more assets)
    const allocationSum = orders.length === 0 ? 100 : 
      orders.every(order => !order.allocation || order.allocation === '') ? 100 : 
      orders.reduce((sum, order) => sum + Number(order.allocation || 0), 0);
    
    if (allocationSum === 100) {
      return false;
    }
    
    // Check if there are available assets to add
    if (!hasAvailableAssets(investorIndex)) {
      return false;
    }
    
    // If there are no orders, don't allow resetting assets until at least one is properly filled
    if (orders.length === 0) {
      return false;
    }
    
    // Check if all existing orders have required fields filled
    const hasIncompleteOrders = orders.some(order => {
      // Check if asset is empty
      if (!order.asset || order.asset.trim() === '') {
        return true;
      }
      
      // Check if allocation is empty
      if (!order.allocation || String(order.allocation).trim() === '') {
        return true;
      }
      
      // Check if limit price is required but empty or not positive (for Limit orders)
      if (currentInvestor.orderType === 'LMT' && (!order.limitPrice || order.limitPrice.trim() === '' || Number(order.limitPrice) <= 0)) {
        return true;
      }
      
      return false;
    });
    
    if (hasIncompleteOrders) {
      return false;
    }
    
    return true;
  };

  // Add new asset handler
  const handleAddAsset = (investorIndex) => {
    const currentInvestor = investorOrders[investorIndex];
    const orders = currentInvestor.orders || [];
    
    // Check if there are available assets to add
    const usedAssets = orders.map(order => order.asset).filter(asset => asset && asset.trim() !== '') || [];
    const availableAssets = assetOptions.filter(option => !usedAssets.includes(option.value));
    
    if (availableAssets.length === 0) {
      return; // No available assets to add
    }
    
    // If there are no orders, don't allow adding assets until at least one is properly filled
    if (orders.length === 0) {
      return;
    }
    
    // Check if all existing orders have required fields filled
    const hasIncompleteOrders = orders.some(order => {
      // Check if asset is empty
      if (!order.asset || order.asset.trim() === '') {
        return true;
      }
      
      // Check if allocation is empty
      if (order.allocation === '' || order.allocation === null || order.allocation === undefined || String(order.allocation).trim() === '') {
        return true;
      }
      
      // Check if limit price is required but empty or not positive (for Limit orders)
      if (currentInvestor.orderType === 'LMT' && (!order.limitPrice || order.limitPrice.trim() === '' || Number(order.limitPrice) <= 0)) {
        return true;
      }
      
      return false;
    });
    
    if (hasIncompleteOrders) {
      return; // Don't add new asset if previous ones are incomplete
    }
    
    setInvestorOrders(prev => {
      const updatedInvestors = [...prev];
      const currentInvestor = updatedInvestors[investorIndex];
      
      if (!currentInvestor.orders) {
        currentInvestor.orders = [];
      }
      
      const newOrder = {
        orderType: currentInvestor.orderType || '',
        asset: '',
        allocation: '',
        limitPrice: '',
        limitPriceFocused: false
      };
      
      currentInvestor.orders.push(newOrder);
      return updatedInvestors;
    });
  };

  // Notify parent of allocation error and canProceed status
  useEffect(() => {
    const hasErrors = hasAllocationErrors();
    const canProceed = hasValidAllocations();
    
    if (onAllocationError) onAllocationError(hasErrors);
    if (onCanProceedChange) onCanProceedChange(canProceed);
  }, [investorOrders, onAllocationError, onCanProceedChange]);

  return (
    <Box sx={{ mb: 3, width: '100%', overflowY: 'auto' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      ) : (
        investorOrders.map((inv, investorIndex) => {
          const investorName = inv?.userRole?.user?.userInfo?.firstName || "-";
          const orders = inv.orders || [];
          const allocationSum = getInvestorAllocationSum(orders);
          const errorMessage = getInvestorErrorMessage(inv);
          const hasDeletedAsset = hasDeletedAssetPerInvestor[investorIndex] || false;
          
          return (
            <Paper key={`investor-${investorIndex}`} sx={{ mb: 3, p: 2, borderRadius: 2, border: errorMessage ? '2px solid red' : '1px solid #e0e0e0', boxShadow: 0 }}>
                             <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                   <Typography variant="body1" sx={{ color: '#444', fontWeight: 400, }}>{investorName}</Typography>
                   {errorMessage && (
                     <Box sx={{ display: 'flex', alignItems: 'center', color: '#f44336' }}>
                       <ErrorOutlineIcon sx={{ fontSize: 20, mr: 0.5 }} />
                       <Typography sx={{ fontWeight: 500, fontSize: 16 }}>
                         {errorMessage}
                       </Typography>
                     </Box>
                   )}
                 </Box>
                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="medium" sx={{ minWidth: 180 }}>
                    <InputLabel>Order Type</InputLabel>
                    <Select
                      label="Order Type"
                      value={inv.orderType || ''}
                      onChange={e => handleInvestorOrderTypeChange(investorIndex, e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          border: 'none'
                        },
                        '& .MuiSelect-select': {
                          color: '#666',
                          padding: '12px 14px',
                          fontSize: '16px'
                        }
                      }}
                    >
                       <MenuItem value="MKT">Market</MenuItem>
                       <MenuItem value="LMT">Limit</MenuItem>
                       {/* <MenuItem value="TWAP">TWAP</MenuItem> */}
                     </Select>
                   </FormControl>
                                      <Button 
                      startIcon={<DeleteOutlineIcon />} 
                      disabled={investorOrders.length <= 1}
                      sx={{ 
                        color: investorOrders.length <= 1 ? '#ccc' : '#1976d2', 
                        textTransform: 'none', 
                        fontWeight: 500, 
                        fontSize: 15, 
                        ml: 2, 
                        minWidth: 0,
                        '&:disabled': {
                          color: '#ccc',
                          cursor: 'not-allowed'
                        }
                      }} 
                      onClick={() => handleRemoveInvestor(inv)}
                    >
                      Remove Investor
                    </Button>
                 </Box>
               </Box>
              
              {/* Orders List */}
              {inv.orderType && inv.orderType !== '' && (
                <Box sx={{ mt: 2 }}>
                  {orders.map((order, orderIndex) => (
                    <Box key={`order-${orderIndex}`} sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                      <FormControl sx={{ flex: 1 }} size="medium">
                        <InputLabel id={`asset-label-${investorIndex}-${orderIndex}`}>Asset</InputLabel>
                        <Select
                          labelId={`asset-label-${investorIndex}-${orderIndex}`}
                          value={order.asset}
                          label="Asset"
                          onChange={e => handleOrderFieldChange(investorIndex, orderIndex, 'asset', e.target.value)}
                          sx={{ borderRadius: 1 }}
                        >
                          {assetOptions
                            .filter(option => {
                              // Show all options for the current order (to allow changing selection)
                              // But filter out assets that are already selected in other orders for the same investor
                              const isCurrentOrder = order.asset === option.value;
                              const isUsedInOtherOrders = orders.some((otherOrder, otherIdx) => 
                                otherIdx !== orderIndex && otherOrder.asset === option.value
                              );
                              return isCurrentOrder || !isUsedInOtherOrders;
                            })
                            .map(opt => (
                              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                      <TextField
                        sx={{ flex: 1, borderRadius: 1 }}
                        label="Allocation"
                        value={order.allocation}
                        onChange={e => handleAllocationChange(investorIndex, orderIndex, e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        }}
                        variant="outlined"
                      />
                      {inv.orderType && inv.orderType !== 'MKT' && (
                        <TextField
                          sx={{ flex: 1, borderRadius: 1 }}
                          label="Limit Price"
                          value={order.limitPrice}
                          onChange={e => handleLimitPriceChange(investorIndex, orderIndex, e.target.value)}
                          variant="outlined"
                          onFocus={() => handleOrderFieldChange(investorIndex, orderIndex, 'limitPriceFocused', true)}
                          onBlur={() => handleOrderFieldChange(investorIndex, orderIndex, 'limitPriceFocused', false)}
                          InputProps={{
                            startAdornment: order.limitPrice ? (
                              <InputAdornment position="start">
                                <Typography sx={{ fontWeight: 'bold', color: '#000000' }}>$</Typography>
                              </InputAdornment>
                            ) : null,
                          }}
                        />
                      )}
                      <DeleteOutlineIcon
                        sx={{ 
                          ml: 2, 
                          cursor: orders.length <= 1 ? 'not-allowed' : 'pointer', 
                          color: orders.length <= 1 ? '#ccc' : '#d32f2f',
                          opacity: orders.length <= 1 ? 0.5 : 1
                        }}
                        onClick={() => orders.length > 1 && handleRemoveOrder(investorIndex, orderIndex)}
                      />
                    </Box>
                  ))}
                  
                  {/* Add Order and Reset Buttons */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 2 }}>
                  <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => handleAddAsset(investorIndex)}
              disabled={!canAddAsset(investorIndex)}
              sx={{
                border: '1px solid #1976d2',
                color: '#1976d2',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: 16,
                borderRadius: 2,
                px: 3,
                py: 1,
                '&:hover': {
                  border: '1px solid #1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                },
                '&:disabled': {
                  border: '1px solid #ccc',
                  color: '#ccc'
                }
              }}
            >
              Add Asset
            </Button>
                    <Button
                      variant="outlined"
                      onClick={() => handleReset(investorIndex)}
                      disabled={!canResetAsset(investorIndex)}
                      sx={{
                        border: '1px solid #1976d2',
                        color: '#1976d2',
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: 16,
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        '&:hover': {
                          border: '1px solid #1976d2',
                          backgroundColor: 'rgba(102, 102, 102, 0.04)'
                        },
                        '&:disabled': {
                          border: '1px solid #ccc',
                          color: '#ccc',
                          cursor: 'not-allowed'
                        }
                      }}
                    >
                      Reset Asset
                    </Button>
                  </Box>
                  
                  {/* Total Allocation Display */}
                  <Typography 
                    color={allocationSum === 100 ? 'primary' : 'error'} 
                    sx={{ mt: 2, fontWeight: 500 }}
                  >
                    Total: {allocationSum}%
                  </Typography>
                </Box>
              )}
            </Paper>
          );
        })
      )}
      
      {/* Remove Investor Confirmation Modal */}
      <Dialog
        open={removeModalOpen}
        onClose={handleCloseRemoveModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          fontWeight: 600, 
          fontSize: '20px', 
          color: '#000',
          pb: 1
        }}>
          Remove investor?
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography sx={{ 
            fontSize: '16px', 
            color: '#666',
            lineHeight: 1.5
          }}>
            Are you sure you would like to remove{' '}
            <span style={{ fontWeight: 600, color: '#000' }}>
              {investorToRemove?.userRole?.user?.userInfo?.firstName || 'this investor'}?
            </span>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          px: 3, 
          pb: 3, 
          gap: 2,
          justifyContent: 'flex-end'
        }}>
          <Button
            onClick={handleCloseRemoveModal}
            sx={{
              border: '1px solid #e0e0e0',
              color: '#000',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              px: 3,
              py: 1,
              '&:hover': {
                border: '1px solid #e0e0e0',
                backgroundColor: '#f5f5f5'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRemoveInvestor}
            sx={{
              backgroundColor: '#d32f2f',
              color: '#fff',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              px: 3,
              py: 1,
              '&:hover': {
                backgroundColor: '#b71c1c'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DifferentAllocationOrders; 